import React, { useState, useEffect } from 'react';

const CheckInHistoryModal = ({ member, locationId, onClose, colors }) => {
    const [checkInHistory, setCheckInHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [memberHistory, setMemberHistory] = useState(null);

    useEffect(() => {
        const loadCheckInHistory = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/history/${locationId}/${member.id}`);
                if (response.ok) {
                    const data = await response.json();
                    console.log('Check-in history response:', data);
                    setMemberHistory(data);
                    
                    // Convert checkin_dates to detailed records
                    const historyRecords = (data.checkin_dates || []).map((date, index) => ({
                        checkin_time: `${date}T12:00:00.000Z`, // Default to noon for date calculations
                        checkin_date: date,
                        id: `history-${index}`
                    }));
                    
                    // Sort by date descending (newest first)
                    historyRecords.sort((a, b) => new Date(b.checkin_date) - new Date(a.checkin_date));
                    setCheckInHistory(historyRecords);
                }
            } catch (error) {
                console.error('Failed to load check-in history:', error);
            } finally {
                setLoading(false);
            }
        };

        if (member && locationId) {
            loadCheckInHistory();
        }
    }, [member, locationId]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    if (!member) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: colors.card,
                padding: '32px',
                borderRadius: '16px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ color: colors.text, margin: 0, fontFamily: 'Poppins', fontSize: '24px' }}>
                        📅 Check-In History - {member.contactName}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: colors.textLight
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Summary Stats */}
                {memberHistory && (
                    <div style={{
                        backgroundColor: colors.secondary,
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '16px',
                        textAlign: 'center'
                    }}>
                        <div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: colors.primary }}>
                                {memberHistory.total_checkins}
                            </div>
                            <div style={{ fontSize: '12px', color: colors.textLight }}>Total Check-ins</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: colors.success }}>
                                {memberHistory.current_streak}
                            </div>
                            <div style={{ fontSize: '12px', color: colors.textLight }}>Current Streak</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: colors.warning }}>
                                {memberHistory.longest_streak}
                            </div>
                            <div style={{ fontSize: '12px', color: colors.textLight }}>Longest Streak</div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
                        <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
                        Loading check-in history...
                    </div>
                ) : checkInHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
                        <div style={{ fontSize: '32px', marginBottom: '16px' }}>📝</div>
                        No check-ins found for this member.
                    </div>
                ) : (
                    <div style={{ 
                        flex: 1,
                        overflowY: 'auto',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '16px',
                            padding: '16px',
                            backgroundColor: colors.secondary,
                            fontWeight: '600',
                            color: colors.text,
                            fontFamily: 'Poppins',
                            borderBottom: `1px solid ${colors.border}`
                        }}>
                            <div>Check-in Date</div>
                            <div>Days Ago</div>
                        </div>
                        
                        {checkInHistory.map((checkin, index) => {
                            const checkinDate = new Date(checkin.checkin_date);
                            const today = new Date();
                            const diffTime = today - checkinDate;
                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                            
                            return (
                                <div key={index} style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px',
                                    padding: '16px',
                                    borderBottom: index < checkInHistory.length - 1 ? `1px solid ${colors.border}` : 'none',
                                    fontFamily: 'Poppins'
                                }}>
                                    <div style={{ color: colors.text }}>
                                        {formatDate(checkin.checkin_date)}
                                    </div>
                                    <div style={{ color: colors.textLight }}>
                                        {diffDays === 0 ? 'Today' : 
                                         diffDays === 1 ? 'Yesterday' : 
                                         `${diffDays} days ago`}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: colors.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            fontFamily: 'Poppins',
                            fontWeight: '500'
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CheckInHistoryModal;